import {
  useQuery as useZeroQuery,
  type UseQueryOptions,
} from "@rocicorp/zero/react";
import { useContext, useEffect, useRef } from "react";
import { NavigationTracerContext } from "../components/NavigationTracer/context";
import type { TracedQuery } from "../components/NavigationTracer/tracer";
import { isTelemetryActive, trackQueryComplete } from "../lib/tracing";
import { reportZeroDefinitionNotFound } from "../lib/reportZeroDefinitionNotFound";

const deferredMark = Symbol("deferredQuery");

/**
 * Pass as the options argument to declare a query deliberately held off the
 * page-load critical path. A query that is merely waiting for an id is not
 * deferred — it still gates the page load — and the two are indistinguishable
 * from inside this hook, so the deferring caller has to say so.
 */
export const DEFERRED = { [deferredMark]: true } as UseQueryOptions;

function isDeferred(options: unknown): boolean {
  return (
    typeof options === "object" && options !== null && deferredMark in options
  );
}

function isEnabled(options: unknown): boolean {
  if (typeof options === "boolean") return options;
  if (typeof options === "object" && options !== null) {
    return (options as { enabled?: boolean }).enabled ?? true;
  }
  return true;
}

function estimateSize(data: unknown): number {
  try {
    return JSON.stringify(data)?.length ?? 0;
  } catch {
    return 0;
  }
}

function rowCount(data: unknown): number {
  if (Array.isArray(data)) return data.length;
  if (data != null) return 1;
  return 0;
}

function extractQueryName(query: unknown): string {
  if (query == null) return "unknown";
  const q = query as Record<string, unknown>;
  if (q.query != null) {
    const inner = q.query as Record<string, unknown>;
    if (typeof inner.queryName === "string") return inner.queryName;
  }
  if (typeof q.queryName === "string") return q.queryName;
  return "unknown";
}

export const useQuery = ((...args: unknown[]) => {
  const result = (useZeroQuery as Function)(...args);
  const [data, details] = result;
  const navigationTracer = useContext(NavigationTracerContext);
  const active = args[0] != null && isEnabled(args[1]);
  // A deferred query is deliberately off the critical path (PR #8936), so it
  // must not gate the navigation — it's traced by neither add nor resolve.
  const traceable = active && !isDeferred(args[1]);

  // A query can mount inactive (no id yet, or explicitly disabled) and activate
  // later; measurement starts at activation so the wait doesn’t inflate durations.
  const startTime = useRef<number | undefined>(undefined);
  const completed = useRef(false);
  const tracedQuery = useRef<TracedQuery | undefined>(undefined);
  // Track initial result type to detect cache hits: if the very first
  // observed type is 'complete', the data was already in Zero's client store.
  const initialType = useRef<string | undefined>(undefined);
  // First time `data` contained rows. Lets us separate "server response
  // arrived" from "type flipped to complete" — the gap between them is
  // purely client-side flush latency.
  const firstDataMs = useRef<number | undefined>(undefined);

  // Measurement starts at first activation (an id arriving, or `enabled`
  // flipping on), so the inactive wait doesn't inflate durations.
  useEffect(() => {
    if (active) startTime.current ??= performance.now();
  }, [active]);

  // Trace the query into the current navigation while it's traceable. A
  // deferred query is skipped, so it can't hold the navigation open.
  useEffect(() => {
    if (!traceable) return;
    const query = navigationTracer.addQuery();
    tracedQuery.current = query;
    return () => query.remove();
  }, [traceable, navigationTracer]);

  // Capture first committed type and first committed non-empty data. Done in
  // an effect (not render) so discarded/interrupted renders don't skew the
  // timestamps, and refs only update for committed renders.
  useEffect(() => {
    if (startTime.current === undefined) return;
    if (details?.type && initialType.current === undefined) {
      initialType.current = details.type;
    }
    if (firstDataMs.current === undefined && rowCount(data) > 0) {
      firstDataMs.current = performance.now() - startTime.current;
    }
  }, [details, data]);

  // First settle only. `error` counts: an errored query must resolve its slot
  // too, or one failure would hold the navigation open forever.
  /* oxlint-disable react-hooks/exhaustive-deps -- args is stable by contract */
  useEffect(() => {
    if (details?.type !== "complete" && details?.type !== "error") return;
    if (completed.current || startTime.current === undefined) return;
    completed.current = true;

    const cached = initialType.current === "complete";
    // Only traced queries belong to the navigation; a deferred query going
    // uncached must not flip the navigation cold.
    if (!cached && traceable) navigationTracer.markCold();
    tracedQuery.current?.resolve();

    // Past here is the ui.query span, which only a materialized query gets.
    if (details.type === "error") {
      reportZeroDefinitionNotFound(details.error);
      return;
    }
    if (!isTelemetryActive()) return;
    const navigationStartTime = navigationTracer.startedAt();
    trackQueryComplete({
      name: extractQueryName(args[0]),
      durationMs: performance.now() - startTime.current,
      rows: rowCount(data),
      bytes: estimateSize(data),
      cached,
      firstDataMs: firstDataMs.current,
      duringNavigation: navigationStartTime !== undefined,
      deferred: isDeferred(args[1]),
      navigationStartOffsetMs:
        navigationStartTime !== undefined
          ? startTime.current - navigationStartTime
          : undefined,
    });
  }, [details, data, args[0]]);
  /* oxlint-enable react-hooks/exhaustive-deps */

  return result;
}) as typeof useZeroQuery;
